import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import DashboardInitialLoading from '../components/ui/DashboardInitialLoading';

interface DashboardLoadingContextType {
  isInitialLoading: boolean;
}

const DashboardLoadingContext = createContext<DashboardLoadingContextType>({
  isInitialLoading: false,
});

export const useDashboardLoading = () => {
  return useContext(DashboardLoadingContext);
};

interface DashboardLoadingProviderProps {
  children: React.ReactNode;
}

export const DashboardLoadingProvider: React.FC<DashboardLoadingProviderProps> = ({ children }) => {
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [hasEverLoaded, setHasEverLoaded] = useState(false);

  // Monitor Redux state
  const { status: settingsStatus } = useSelector((state: RootState) => state.setting);
  const { data: { cryptoWallets } } = useSelector((state: RootState) => state.assets);

  useEffect(() => {
    // Only show loading on the very first load when entering dashboard
    if (!hasEverLoaded && settingsStatus === 'succeeded') {
      // Check if we need to load wallet data
      if (cryptoWallets.status === 'idle') {
        setIsInitialLoading(true);
      }
    }
  }, [settingsStatus, cryptoWallets.status, hasEverLoaded]);

  useEffect(() => {
    // Hide loading when data is ready
    if (isInitialLoading && cryptoWallets.status === 'succeeded') {
      const timer = setTimeout(() => {
        setIsInitialLoading(false);
        setHasEverLoaded(true);
      }, 1000); // Smooth transition

      return () => clearTimeout(timer);
    }
  }, [isInitialLoading, cryptoWallets.status]);

  // Reset only when user logs out (clean slate for next user)
  useEffect(() => {
    // This will reset when the component unmounts (user navigates away from dashboard area)
    return () => {
      setHasEverLoaded(false);
      setIsInitialLoading(false);
    };
  }, []);

  const contextValue: DashboardLoadingContextType = {
    isInitialLoading,
  };

  return (
    <DashboardLoadingContext.Provider value={contextValue}>
      <DashboardInitialLoading
        isVisible={isInitialLoading}
        loadingMessage="Loading your wallets and balances..."
      />
      {children}
    </DashboardLoadingContext.Provider>
  );
};